import { QuestionOutlined } from "@ant-design/icons"
import { App, Button, Flex } from "antd"
import { type ReactNode, useEffect } from "react"
import { useNavigate } from "react-router-dom"

import { RoutesPaths } from "../../constants/routes.ts"
import { LEADS_SAVE_RESULT } from "../../containers/Leads/constants/leads-save-results.ts"
import { readProjectManagerRequired } from "../../containers/Leads/helpers/project-manager-required.ts"
import { useProjectManagerPrompt } from "../../containers/Leads/hooks/use-project-manager-prompt.tsx"
import { leadsSetProp } from "../../containers/Leads/redux/leads.slice.ts"
import { changeLeadStatus } from "../../containers/Leads/redux/leads.thunks.ts"
import { type LeadListItem } from "../../containers/Leads/types/types.ts"
import { useAppDispatch } from "../use-app-dispatch.ts"
import { useAppSelector } from "../use-app-selector.ts"
import { useNotifications } from "../use-notifications.ts"

interface LeadMissingFieldsRejection {
  targetStatus?: number | null
  /** The backend's own field names — what the deep link carries. */
  fields?: string[]
  missing?: string[]
  leadId?: string
}

interface UseLeadStatusChangeArgs {
  refetchCallback: () => void
  /** A surface already showing this lead answers the rejection in place. */
  onMissingFields?: (rejection: LeadMissingFieldsRejection) => void
  /** The user declined to pick a PM, so the move to Won is not happening. */
  onProjectManagerPromptCancel?: () => void
}

export const useLeadStatusChange = ({
  refetchCallback,
  onMissingFields,
  onProjectManagerPromptCancel
}: UseLeadStatusChangeArgs): {
  confirmStatusChange: (
    lead: Pick<LeadListItem, "id">,
    targetStatus: number
  ) => void
  /** The same change without the confirmation, for a move already confirmed. */
  changeStatus: (leadId: string, targetStatus: number) => void
  /** Render this wherever the hook is used — the PM prompt a Won can raise. */
  statusChangeDialogs: ReactNode
} => {
  const { modal } = App.useApp()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const notification = useNotifications()

  const saveResult = useAppSelector((state) => state.leads.saveResult)
  const currentUserId = useAppSelector((state) => state.auth?.userInfo?.id)

  // A Won the server answered with "choose the PM first" — Settings >
  // Configurations' Automatic Project Creation is on, so this lead is about
  // to become a project. The move is resent once a PM is picked; cancelling
  // leaves the lead where it was.
  const projectManagerPrompt = useProjectManagerPrompt<{
    leadId: string
    targetStatus: number
  }>({
    submit: ({ leadId, targetStatus }, projectManagerId) =>
      dispatch(
        changeLeadStatus({
          id: leadId,
          target_status: targetStatus,
          prompt_project_manager: true,
          project_manager_id: projectManagerId
        })
      ).unwrap(),
    onCancel: onProjectManagerPromptCancel,
    defaultProjectManagerId: currentUserId
  })

  const changeStatus = (leadId: string, targetStatus: number) => {
    dispatch(
      changeLeadStatus({
        id: leadId,
        target_status: targetStatus,
        prompt_project_manager: true
      })
    )
      .unwrap()
      .catch((rejection: unknown) => {
        // Every other refusal was already reported by the thunk.
        const required = readProjectManagerRequired(rejection)
        if (required) {
          projectManagerPrompt.ask(required, { leadId, targetStatus })
        }
      })
  }

  const confirmStatusChange = (
    lead: Pick<LeadListItem, "id">,
    targetStatus: number
  ) => {
    modal.confirm({
      centered: true,
      icon: <QuestionOutlined />,
      title: "Confirm Status Change",
      content: "Are you sure you want to change the status of this lead?",
      cancelText: "Cancel",
      okText: "Change",
      onOk() {
        changeStatus(lead?.id, targetStatus)
      }
    })
  }

  useEffect(() => {
    if (saveResult) {
      const type = saveResult?.type

      if (type === LEADS_SAVE_RESULT.MISSING_FIELDS) {
        const leadId = saveResult.leadId

        onMissingFields?.({
          targetStatus: saveResult.targetStatus,
          fields: saveResult.fields,
          missing: saveResult.missing,
          leadId
        })

        const description = (
          <Flex vertical>
            <span>Some required fields are missing.</span>
            {saveResult.missing?.length ? (
              <ul className="mb-0 list-disc pl-5">
                {saveResult.missing.map((field) => (
                  <li key={field}>{field}</li>
                ))}
              </ul>
            ) : null}
            <span>Would you like to add the missing information now?</span>
          </Flex>
        )

        notification.warning({
          message: "Required Information Missing",
          description,
          showProgress: true,
          pauseOnHover: true,
          // Without a lead to open, the action would navigate nowhere.
          actions: leadId ? (
            <Button
              onClick={() => {
                // The field names travel with the link so the workspace can
                // mark the cards the message named, rather than matching on the
                // labels, which reword.
                const missingFields = saveResult?.fields?.length
                  ? `&missing=${encodeURIComponent(saveResult.fields.join(","))}`
                  : ""
                navigate(
                  `${RoutesPaths.lead.view(leadId)}?next_status=${saveResult?.targetStatus}${missingFields}`
                )
                notification.destroy()
              }}
              type="primary"
              size="small"
            >
              Update Now
            </Button>
          ) : undefined
        })
      }

      if (type === LEADS_SAVE_RESULT.LEAD_STATUS_UPDATE) {
        refetchCallback()
      }

      dispatch(leadsSetProp({ key: "saveResult", value: null }))
    }
  }, [saveResult])

  return {
    confirmStatusChange,
    changeStatus,
    statusChangeDialogs: projectManagerPrompt.dialog
  }
}
